<template>
    <Skeleton v-if="initialDataLoader.pending.value" :items="['block', `${APEX_SKELETON}`]"/>
    <template v-else>
        <Blocks>
            <Block v-if="isSet(monthRanger.selectedRenderSet.value.bookings)" color="var(--ion-color-grey-shade)">
                <template #bigNumber>{{bookingsLabel}}</template>
                <template #subTitle>{{bookingsSubtitle}}</template>
            </Block>

            <Block v-if="isSet(monthRanger.selectedRenderSet.value.productiveHours)" color="var(--ion-color-grey-shade)">
                <template #bigNumber>{{floatFormat(monthRanger.selectedRenderSet.value.productiveHours, 1)}}h</template>
                <template #subTitle>Produktiv&shy;stunden</template>
            </Block>

            <Block v-if="isSet(monthRanger.selectedRenderSet.value.revenue)">
                <template #bigNumber>{{money.formatLargeMoney(monthRanger.selectedRenderSet.value.revenue)}}</template>
                <template #subTitle>Umsatz</template>
            </Block>
        </Blocks>

        <IonButton
            class="chart-toggle"
            color="light"
            expand="block"
            @click="showRevenueChart = !showRevenueChart"
        >
            Details {{showRevenueChart ? 'ausblenden' : 'anzeigen'}}
        </IonButton>

        <div v-if="showRevenueChart" class="boxed margin-bottom-regular">
            <ApexChartValueLine :values="[
                {
                    label: 'Produktiv',
                    value: monthRanger.selectedRenderSet.value.productiveHours,
                    formatter: value => formatHours(value as number),
                    color: revenueChartColors.productiveHours
                },
                {
                    label: 'AZK',
                    value: monthRanger.selectedRenderSet.value.azkDays,
                    formatter: value => value ? value + ' Tage' : '-',
                    color: revenueChartColors.azk
                },
                {
                    label: 'Krank',
                    value: monthRanger.selectedRenderSet.value.sicknessDays,
                    formatter: value => value ? value + ' Tage' : '-',
                    color: revenueChartColors.sickness
                },
                {
                    label: 'Urlaub',
                    value: monthRanger.selectedRenderSet.value.vacationDays,
                    formatter: value => value ? value + ' Tage' : '-',
                    color: revenueChartColors.vacation
                }
            ]"/>
            <ApexChartWrapper :options="apexOptions"/>
        </div>
    </template>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watch} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {EmployeeMonthSummary, EmployeeMonthSummaryFragment, PayrollStatus} from "@/graphql/generated/graphql";

import {kapitelMonthString} from '@/graphql/kapitelTypes';
import {APEX_SKELETON, generateApexOptions} from "@/helper/apexchart";
import {fetchEmployeeMonthSummaries} from "@/helper/employeeMonthSummary";
import {fetchPayrollStatusList} from "@/helper/monthKPI/monthKpiPayroll";
import {MonthKPIApexMonthRangerFactory} from "@/helper/monthKPI/monthKPI";
import {toMap} from "@/helper/array";
import {isSet, nullish} from "@/helper/isset";
import {floatFormat, pluralizeUnitOnly} from "@/helper/amountFormatter";
import * as money from "@/helper/money";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import Blocks from "@/views/components/Blocks.vue";
import Block from "@/views/components/Block.vue";
import {IonButton} from "@ionic/vue";

// props
const selectedMonth = defineModel({
    type: String as PropType<kapitelMonthString>,
    required: true,
})

// loader
const initialDataLoader = new DataLoader();
const showRevenueChart = ref(false);

const revenueChartColors = {
    productiveHours: 'var(--ion-color-primary)',
    azk: 'var(--ion-color-grey-tint-1-shade)',
    sickness: 'var(--ion-color-grey-tint-1-shade)',
    vacation: 'var(--ion-color-grey-tint-1-shade)',
}
const colors = [
    revenueChartColors.productiveHours,
    revenueChartColors.azk,
    revenueChartColors.sickness,
    revenueChartColors.vacation,
]

const formatHours = (value: number|nullish) => isSet(value) ? `${floatFormat(value, 1)}h` : '-'

/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    selected: selectedMonth.value,
    renderer: (calendarMonth: CalendarMonth) => {
        const employeeMonthSummary = employeeMonthSummaryMap.value.get(calendarMonth.getId())
        const payrollStatus = payrollStatusMap.value.get(calendarMonth.getId())

        const absenceDayHoursFloat = employeeMonthSummary?.absenceDayHoursFloat ?? 0

        return {
            productiveHours: employeeMonthSummary?.productiveHours ?? null,
            azkHours: isSet(employeeMonthSummary?.cntAZKDays) ? employeeMonthSummary.cntAZKDays * absenceDayHoursFloat : null,
            sicknessHours: isSet(employeeMonthSummary?.cntSicknessDays) ? employeeMonthSummary.cntSicknessDays * absenceDayHoursFloat : null,
            vacationHours: isSet(employeeMonthSummary?.cntVacationDays) ? employeeMonthSummary.cntVacationDays * absenceDayHoursFloat : null,

            bookings: employeeMonthSummary?.cntBookings ?? null,
            targetBookings: employeeMonthSummary?.bookingTarget ?? null,
            azkDays: isSet(employeeMonthSummary?.cntAZKDays) ? employeeMonthSummary.cntAZKDays : null,
            sicknessDays: isSet(employeeMonthSummary?.cntSicknessDays) ? employeeMonthSummary.cntSicknessDays : null,
            vacationDays: isSet(employeeMonthSummary?.cntVacationDays) ? employeeMonthSummary.cntVacationDays : null,

            revenue: payrollStatus?.revenue ?? null,
        }
    }
})

watch(() => monthRanger.selected.value, (month: kapitelMonthString) => selectedMonth.value = month)

const bookingsLabel = computed(() => {
    const renderSet = monthRanger.selectedRenderSet.value
    return !isSet(renderSet.targetBookings) || !isSet(renderSet.bookings) || renderSet.bookings >= renderSet.targetBookings
        ? renderSet.bookings
        : `${renderSet.bookings} / ${renderSet.targetBookings}`
})
const bookingsSubtitle = computed(() => {
    const renderSet = monthRanger.selectedRenderSet.value
    const count = !isSet(renderSet.targetBookings) || !isSet(renderSet.bookings) || renderSet.bookings >= renderSet.targetBookings
        ? renderSet.bookings
        : renderSet.targetBookings

    return isSet(count) ? pluralizeUnitOnly(count, 'Dienst', 'Dienste') : ''
})

const hourSeries = computed(() => ([
    {
        name: "Produktivstunden",
        data: monthRanger.allMonthsRenderSets.map(rs => rs.value.productiveHours),
        type: 'bar',
    },
    {
        name: "AZK-Ausgleich",
        data: monthRanger.allMonthsRenderSets.map(rs => rs.value.azkHours),
        type: 'bar',
    },
    {
        name: "Krank",
        data: monthRanger.allMonthsRenderSets.map(rs => rs.value.sicknessHours),
        type: 'bar',
    },
    {
        name: "Urlaub",
        data: monthRanger.allMonthsRenderSets.map(rs => rs.value.vacationHours),
        type: 'bar',
    },
]))

const apexOptions = computed(() => {
    return generateApexOptions(
        monthRanger,
        {
            series: hourSeries.value,
            chart: {
                type: 'bar',
                stacked: true
            },
            colors,
            legend: {
                show: false,
            },
            yaxis: {
                labels: {
                    formatter: (value: number|nullish) => formatHours(value),

                    // ensure equal width across all monthKPI charts
                    maxWidth: 50,
                    minWidth: 50
                }
            }
        },
        (idx: number) => {
            const renderSet = monthRanger.getRenderSet(idx)?.value
            const totalHours = [
                renderSet?.productiveHours,
                renderSet?.azkHours,
                renderSet?.sicknessHours,
                renderSet?.vacationHours,
            ].reduce<number>((sum, value) => isSet(value) ? sum + value : sum, 0)

            return totalHours > 0 ? formatHours(totalHours) : null
        }
    )
})

/**
 * raw data
 */
const employeeMonthSummaries = ref<EmployeeMonthSummaryFragment[]>([])
const employeeMonthSummaryMap = computed(() => toMap(employeeMonthSummaries.value, (a:EmployeeMonthSummaryFragment) => a.month as kapitelMonthString))
initialDataLoader.add(
    async () => employeeMonthSummaries.value = await fetchEmployeeMonthSummaries(monthRanger.min, monthRanger.max)
)

const payrollStatusList = ref<PayrollStatus[]>([])
const payrollStatusMap = computed(() => toMap(payrollStatusList.value, (a) => a.month as kapitelMonthString))
initialDataLoader.add(
    async () => payrollStatusList.value = await fetchPayrollStatusList(monthRanger.allMonths)
)

/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>
